import { Address, Seller, TPrice, TSize } from "./index";
import { FilterModel } from "@hooks/useFiltersSearchParams";
import { getParams, sellerApiClient } from "@api/apiClient/sellerApiClient";
import { PaginationModel } from "@hooks/usePagingSearchParams";
import { useQuery } from "react-query";

const defaultParams: PaginationModel = { page: 1, pageSize: 1 };

export type TRequestParams = {
  filters?: FilterModel[];
  sorting?: { column: string; direction: string };
  page: PaginationModel;
};

export type TNewOrdersParcel = {
  id: string;
  pickUpCost: {
    amount: number;
    currency: string;
  };
  requestNumber: string;
  seller: Seller;
  timeslotFrom: Date;
  weight: {
    unit: string;
    value: number;
  };
};

export type TNewOrdersParcelsResponse = {
  total: number;
  items: TNewOrdersParcel[];
};

export const getNewOrders = async (
  params: TRequestParams,
): Promise<TNewOrdersParcelsResponse> => {
  const { filters, page } = params;
  const response = await sellerApiClient.get<TNewOrdersParcelsResponse>(
    "/v1/fbp-pick-up/new-request-forms/page",
    {
      params: getParams(page, filters),
      paramsSerializer: { indexes: null },
    },
  );
  return response.data;
};

export const useNewOrdersCounter = (enabled: boolean = true) => {
  const { data, refetch } = useQuery({
    queryKey: ["new-orders", defaultParams],
    queryFn: () =>
      getNewOrders({
        filters: [],
        page: defaultParams,
      }),
    retry: 1,
    select: ({ total }) => total ?? 0,
    enabled: enabled,
  });
  return { data, refetch };
};

export type TFBPPaidTariffSettings = {
  countryCode: string;
  fbpPickupMinPrice: number;
  fbpPricePerKg: number;
  id: number;
};

export type TFBPInfo = {
  isNew: boolean;
  paidFbpTariffSettings: TFBPPaidTariffSettings[];
};

export type TFBPInfoResponse = TFBPInfo;

export const getFBPInfo = async (): Promise<TFBPInfoResponse> => {
  const response = await sellerApiClient.get("/v1/fbp-pick-up/info");
  return response.data;
};

// Тариф Pick Up свой для каждого fbpWarehouse, приходит с бэкенда.
// Сумма к оплате = общий вес заявки (кг) * pricePerKg.
export type TFBPPickupTariff = {
  fbpWarehouseId: string;
  pricePerKg: number;
  currency: string;
};

export const getFBPPickupTariff = async (
  fbpWarehouseId: string,
): Promise<TFBPPickupTariff> => {
  const response = await sellerApiClient.get("/v1/fbp-pick-up/tariff", {
    params: { fbpWarehouseId },
  });
  return response.data;
};

export type TProductsInFBP = {
  id: string;
  name: string;
  ozonSkuId: string;
  quantity: number;
  url: string;
  vendorCode: string;
};

export type TNewOrdersCard = {
  id: string;
  products: TProductsInFBP[];
  requestNumber: string;
  warehouseAddress: Address;
};

export const getNewOrdersCard = async (id: string): Promise<TNewOrdersCard> => {
  const response = await sellerApiClient.get(
    `/v1/fbp-pick-up/new-request-forms/${id}`,
  );
  return response.data;
};

export type TFBPCost = {
  pickUpCost: {
    amount: number;
    currency: string;
  };
};

export const getFBPCost = async (id: string): Promise<TFBPCost> => {
  const response = await sellerApiClient.get(
    `/v1/fbp-pick-up/request-forms/pickup-cost?requestFormId=${id}`,
  );
  return response.data;
};

export type TFBPOrderAddress = {
  address: Address | undefined;
  comment?: string;
  contactInfo: {
    name: string | undefined;
    phone: string | undefined;
  };
  shipmentDate: string | undefined;
};

export type TFBPOrderParams = TFBPOrderAddress & {
  requestFormId: string;
};

export const sendFBPOrder = async (params: TFBPOrderParams): Promise<void> => {
  const response = await sellerApiClient.post<void>(
    "/v1/fbp-pick-up/pay-request-form",
    params,
  );
  return response.data;
};

export type TCheckFBPOrderStatusResponse = {
  id: string;
  reason?: string;
  status: string;
};

export const checkFBPOrderStatus = async (
  fbpOrderId: string,
): Promise<TCheckFBPOrderStatusResponse> => {
  const response = await sellerApiClient.get(
    `/v1/fbp-pick-up/pay-request-form/order/${fbpOrderId}/status`,
  );
  return response.data;
};

export type TInProcessOrder = {
  address: Address;
  deliveryServiceId: string;
  deliveryServiceName: string;
  deliveryTrackNumber: string;
  id: string;
  parcelsCount: number;
  pickUpCost: {
    amount: number;
    currency: string;
  };
  requestNumber: string;
  sellers: Seller[];
  shipmentDate: Date;
  status: string;
};

export type TInProcessOrdersResponse = {
  total: number;
  items: TInProcessOrder[];
};

export const getInProcess = async (
  params: TRequestParams,
): Promise<TInProcessOrdersResponse> => {
  const { filters, page } = params;
  const response = await sellerApiClient.get<TInProcessOrdersResponse>(
    "/v1/fbp-pick-up/request-forms/page",
    {
      params: getParams(page, filters),
      paramsSerializer: { indexes: null },
    },
  );
  return response.data;
};

export const useInProcessCounter = (enabled: boolean = true) => {
  const { data, refetch } = useQuery({
    queryKey: ["in-process", defaultParams],
    queryFn: () =>
      getInProcess({
        filters: [],
        page: defaultParams,
      }),
    retry: 1,
    select: ({ total }) => total ?? 0,
    enabled: enabled,
  });
  return { data, refetch };
};

export type TInProcessCard = {
  address: Address;
  fullName: string;
  id: string;
  phone: string;
  pickUpCost: {
    amount: number;
    currency: string;
  };
  products: TProductsInFBP[];
  requestNumber: string;
  shipmentDate: Date;
  stockStatus: number;
  warehouseAddress: Address;
  weight: {
    unit: string;
    value: number;
  };
};

export const getInProcessCard = async (id: string): Promise<TInProcessCard> => {
  const response = await sellerApiClient.get(
    `/v1/fbp-pick-up/request-forms/${id}`,
  );
  return response.data;
};

/////
export type TWbWarehouse = {
  id: string;
  wbSellerId: string;
  wbSeller: {
    id: string;
    token: string;
    tokenStatus:
      | "Active"
      | "Expiring"
      | "Expired"
      | "Invalid"
      | "SellerIdMismatch";
    tokenExpiresAt: string;
  };
  warehouseName: string;
  sellerId: string;
  warehouseId: string;
  sellerName: string;
}

export type TAliWarehouse = {
  id: string;
  name: string;
  sellerId: string;
  isAwaitingDeletion: boolean;
  tokenStatus: "Active" | "Invalid";
};

export type TSellerWarehouse = {
  id: string;
  ymWarehouses: {
    id: string;
    name: string;
    ymCampaignId: string;
    sellerId: string;
    placementType: string;
  }[];
  wbWarehouses: TWbWarehouse[];
  aliWarehouses: TAliWarehouse[];
  name: string;
  isAwaitingDeletion: boolean;
};

// Лимиты габаритов товара, принимаемого складом: стороны и сумма — в см, вес брутто — в кг.
export type TProductDimensionLimits = {
  maxLengthCm: number;
  maxWidthCm: number;
  maxHeightCm: number;
  maxSidesSumCm: number;
  maxWeightBruttoKg: number;
};

export type TWarehouse = {
  id: string;
  name: string;
  address: string;
  contactPhone: string;
  contactName: string;
  countryCode: string;
  sellerWarehouses: TSellerWarehouse[];
  openingHours?: string;
  isPickUpAvailable?: boolean;
  fbpAvailableMarketplaces: {
    wildberries: boolean;
    yandex: boolean;
    aliexpress: boolean;
  };
  // null — ограничений габаритов для WB на складе нет
  wbSettings?: {
    productDimensionLimits?: TProductDimensionLimits;
    productDensitySettings?: { minKgM3: number };
  } | null;
  aliSettings?: {
    productDimensionLimits?: TProductDimensionLimits;
    productDensitySettings?: { minKgM3: number };
  } | null;
  ymSettings?: {
    productDimensionLimits?: TProductDimensionLimits;
    productDensitySettings?: { minKgM3: number };
  } | null;
};

export type TWarehouses = {
  fbpWarehouses: TWarehouse[];
};

export const getWarehouses = async (): Promise<TWarehouses> => {
  const response = await sellerApiClient.get("/v1/fbp/warehouses");
  return response.data;
};

export type TYmSeller = {
  id: string;
  name: string;
  placementType?: string;
};

export type TYmSellers = {
  ymSellers: TYmSeller[];
};

export const getYmSeller = async (
  fbpWarehouseId: string,
): Promise<TYmSeller[]> => {
  const response = await sellerApiClient.get("/v1/fbp/warehouses/ym/sellers", {
    params: { fbpWarehouseId },
  });
  return response.data?.ymSellers;
};

export type TAliShippingTemplate = {
  id: number;
  name: string;
};

export type TAliSeller = {
  id: string;
  name: string;
  // Шаблоны доставки магазина — один из них выбирается при привязке
  shippingTemplates: TAliShippingTemplate[];
};

export type TAliSellers = {
  aliSellers: TAliSeller[];
};

// Магазины Ali, доступные для привязки (все, кроме уже привязанных к этому FBP-складу)
export const getAliSeller = async (
  fbpWarehouseId: string,
): Promise<TAliSellers> => {
  const response = await sellerApiClient.get<TAliSellers>(
    "/v1/fbp/warehouses/ali/sellers",
    {
      params: { fbpWarehouseId },
    },
  );
  return response.data;
};

export type TWbSeller = {
  wbId: string;
  name: string;
};

export type TWbSellers = {
  wbWarehouses: TWbSeller[];
};

export const getWbSeller = async ({
  wbSellerId,
  fbpWarehouseId,
}: {
  wbSellerId: string;
  fbpWarehouseId: string;
}): Promise<TWbSeller[]> => {
  const response = await sellerApiClient.get(
    `/v1/fbp/warehouses/wb/${wbSellerId}`,
    {
      params: { fbpWarehouseId },
    },
  );
  return response.data?.wbWarehouses;
};

export type TNewSellerData = {
  fbpWarehouseId: string;
  wbInfos?: {
    wbSellerId: string;
    wbId: string;
  }[];
  ymSellerIds?: string[];
  aliInfos?: {
    aliSellerId: string;
    shippingTemplateId: number;
  }[];
  name: string;
};

export const createFbpSeller = async (data: TNewSellerData): Promise<void> => {
  const response = await sellerApiClient.post(
    "/v1/fbp/warehouses/seller/create",
    data,
  );
  return response.data;
};

export type TUpdateSellerData = {
  sellerWarehouseId: string;
  wbInfos?: {
    wbSellerId: string;
    wbId: string;
  }[];
  ymSellerIds?: string[];
  aliInfos?: {
    aliSellerId: string;
    shippingTemplateId: number;
  }[];
  name: string;
};

export const updateFbpSeller = async (
  data: TUpdateSellerData,
): Promise<void> => {
  const response = await sellerApiClient.put(
    "/v1/fbp/warehouses/seller/update",
    data,
  );
  return response.data;
};

export type TItemCommon = {
  id: string;
  url: string;
  primaryImage: string;
  price?: TPrice;
  name: string;
  marketplace: string;
  dimensions?: {
    length: TSize;
    width: TSize;
    height: TSize;
  };
  weight?: TSize;
  isSuspicious?: boolean;
  count?: number;
  errorType?: string;
  sellerName?: string;
};

export type TYmItem = TItemCommon & {
  ymOfferId: string;
  sku: string;
};

export type TWbItem = TItemCommon & {
  wbNmId: string;
  wbImtId?: string;
  vendorCode?: string;
  wbTechSize?: string;
  wbSize?: string;
};

export type TAliItem = TItemCommon & {
  aliItemId: string;
  skuId: string;
  skuCode: string;
};

export type TUnknownItem = {
  marketplace: string;
  sku: string;
  count?: number;
  techSize?: string;
  id: string;
};

export type TRequestItemKnown = TYmItem | TWbItem | TAliItem;
export type TRequestItem = TYmItem | TWbItem | TAliItem | TUnknownItem;

type TSearchCommon = {
  matchingLinkId?: string
  density: {
    unit: string;
    value: number;
  };
}

export type TRequestItemKnownExtended = TItemCommon & TRequestItemKnown;
export type TYmItemSearch = TItemCommon & TYmItem & TSearchCommon;
export type TWbItemSearch = TItemCommon & TWbItem & TSearchCommon;
export type TAliItemSearch = TItemCommon & TAliItem & TSearchCommon;
export type TValidationRequestItem = TRequestItem & {
  density?: {
    unit: string;
    value: number;
  };
};

export type TSearchItems = {
  ymItems: TYmItemSearch[];
  wbItems: TWbItemSearch[];
  aliItems: TAliItemSearch[];
};

// Ссылки на карточку в выдаче Ali нет — строим из составного item_id целиком ("1_748508454")
export const getAliProductUrl = (aliItemId: string): string =>
  aliItemId ? `https://aliexpress.ru/item/${aliItemId}.html` : "";

export const isYmRequestItem = (value: TRequestItem): value is TYmItem =>
  value.marketplace === "Yandex";
export const isWbRequestItem = (value: TRequestItem): value is TWbItem =>
  value.marketplace === "Wildberries";
export const isAliRequestItem = (value: TRequestItem): value is TAliItem =>
  value.marketplace === "Aliexpress";
export const isUnknownRequestItem = (
  value: TRequestItem,
): value is TUnknownItem => value.marketplace === "unknown";

export const isYmItem = (
  value: TYmItem | TWbItem | TAliItem,
): value is TYmItem =>
  (value as TYmItem).ymOfferId !== undefined ||
  (value as TYmItem).sku !== undefined;
export const isWbItem = (
  value: TYmItem | TWbItem | TAliItem,
): value is TWbItem =>
  (value as TWbItem).wbNmId !== undefined ||
  (value as TWbItem).wbImtId !== undefined ||
  (value as TWbItem).vendorCode !== undefined;

export const isAliItem = (
  value: TYmItem | TWbItem | TAliItem,
): value is TAliItem =>
  (value as TAliItem).aliItemId !== undefined ||
  (value as TAliItem).skuCode !== undefined;

export const searchItems = async ({
  sellerWarehouseId,
  search,
  marketplaceTypes,
  hasMatchingLinks,
}: {
  sellerWarehouseId: string;
  search: string;
  marketplaceTypes?: string[];
  hasMatchingLinks?: boolean;
}): Promise<(TYmItemSearch | TWbItemSearch | TAliItemSearch)[]> => {
  const response = await sellerApiClient.get<TSearchItems>(
    "/v1/fbp/products/search",
    {
      params: {
        sellerWarehouseId,
        search,
        ...(hasMatchingLinks ? { hasMatchingLinks } : {}),
        ...(marketplaceTypes?.length ? { marketplaceTypes } : {}),
      },
      paramsSerializer: { indexes: null },
    },
  );
  return [
    ...response.data.ymItems.map((v) => ({ ...v, marketplace: "Yandex" })),
    ...response.data.wbItems.map((v) => ({ ...v, marketplace: "Wildberries" })),
    ...(response.data.aliItems ?? []).map((v) => ({
      ...v,
      marketplace: "Aliexpress",
      url: getAliProductUrl(v.aliItemId),
    })),
  ];
};

export const searchWbItems = async ({
  sellerWarehouseId,
  search,
  hasMatchingLinks,
}: {
  sellerWarehouseId: string;
  search: string;
  hasMatchingLinks?: boolean;
}): Promise<TWbItemSearch[]> => {
  const response = await sellerApiClient.get<TSearchItems>(
    "/v1/fbp/products/search",
    {
      params: {
        sellerWarehouseId,
        search,
        marketplaceType: "Wildberries",
        ...(hasMatchingLinks ? { hasMatchingLinks } : {}),
      },
    },
  );
  return response.data.wbItems.map((v) => ({
    ...v,
    marketplace: "Wildberries",
  }));
};

export const searchAliItems = async ({
  sellerWarehouseId,
  search,
  hasMatchingLinks,
}: {
  sellerWarehouseId: string;
  search: string;
  hasMatchingLinks?: boolean;
}): Promise<TAliItemSearch[]> => {
  const response = await sellerApiClient.get<TSearchItems>(
    "/v1/fbp/products/search",
    {
      params: {
        sellerWarehouseId,
        search,
        marketplaceType: "Aliexpress",
        ...(hasMatchingLinks ? { hasMatchingLinks } : {}),
      },
    },
  );
  return (response.data.aliItems ?? []).map((v) => ({
    ...v,
    marketplace: "Aliexpress",
    url: getAliProductUrl(v.aliItemId),
  }));
};

export const searchYmItems = async ({
  sellerWarehouseId,
  search,
  hasMatchingLinks,
}: {
  sellerWarehouseId: string;
  search: string;
  hasMatchingLinks?: boolean;
}): Promise<TYmItemSearch[]> => {
  const response = await sellerApiClient.get<TSearchItems>(
    "/v1/fbp/products/search",
    {
      params: {
        sellerWarehouseId,
        search,
        marketplaceType: "Yandex",
        ...(hasMatchingLinks ? { hasMatchingLinks } : {}),
      },
    },
  );
  return response.data.ymItems.map((v) => ({ ...v, marketplace: "Yandex" }));
};

export const getProductLabel = async ({
  sellerWarehouseId,
  printSize,
  id,
}: {
  sellerWarehouseId?: string;
  printSize?: string;
  id: string;
}): Promise<string> => {
  const response = await sellerApiClient.get<string>(
    `/v1/fbp/products/${id}/label`,
    {
      params: { sellerWarehouseId, printSize },
      responseType: "blob",
    },
  );
  return response.data;
};

export const updateYmProducts = async (sellerId: string): Promise<void> => {
  const response = await sellerApiClient.post<void>(
    `/v1/fbp/products/ym/update/${sellerId}`,
  );
  return response.data;
};

export const updateWbProducts = async (sellerId: string): Promise<void> => {
  const response = await sellerApiClient.post<void>(
    `/v1/fbp/products/wb/update/${sellerId}`,
  );
  return response.data;
};

export type TRestoreRequestData = {
  sellerWarehouseId: string;
  boxCount: number;
  ymProducts?: {
    ymProductId: string;
    count: number;
  }[];
  wbProducts?: {
    wbProductId: string;
    count: number;
  }[];
  aliProducts?: {
    aliProductId: string;
    count: number;
  }[];
};

export const createFBPRestockRequest = async (
  data: TRestoreRequestData,
): Promise<{ id: string }> => {
  const response = await sellerApiClient.post<{ id: string }>(
    `/v1/fbp/restock-requests`,
    { ...data },
  );
  return response.data;
};

export const cancelFBPRestockRequest = async (id: string): Promise<void> => {
  const response = await sellerApiClient.post<void>(
    `/v1/fbp/restock-requests/${id}/cancel`,
  );
  return response.data;
};

export type TRestoreRequestsPageItem = {
  id: string;
  number: string;
  ymProductsCount: number;
  wbProductsCount: number;
  aliProductsCount: number;
  ymTotalWeight: TSize;
  wbTotalWeight: TSize;
  aliTotalWeight: TSize;
  createdAt: string;
  status: string;
  isCancelable: boolean;
  shipmentDate?: string;
  shipmentTime?: {
    hour: string;
    minute: string;
  };
  deliveryTrackNumber?: string;
  addressRawLine?: string;
  pickUpStatus?: string;
  isPickUpAvailable?: boolean;
};

export type TRestoreRequestsPageParams = TRequestParams & {
  sellerWarehouseId: string;
};

export type TRestoreRequestsPageResponse = {
  total: number;
  items: TRestoreRequestsPageItem[];
};

export type TYmItemRequest = TItemCommon & TYmItem;
export type TWbItemRequest = TItemCommon & TWbItem;
export type TAliItemRequest = TItemCommon & TAliItem;
export type TFBPRequestDetails = {
  createdAt: string;
  number: string;
  sellerWarehouseId: string;
  boxCount: number;
  ymItems: {
    ymProduct: TYmItemRequest;
    count: number;
  }[];
  wbItems: {
    wbProduct: TWbItemRequest;
    count: number;
  }[];
  aliItems: {
    aliProduct: TAliItemRequest;
    count: number;
  }[];
  status: string;
  isCancelable: boolean;
  shipmentDate?: string;
  shipmentTime?: {
        hour: string;
        minute: string;
      };
  deliveryTrackNumber?: string;
  addressRawLine?: string;
  contactName: string;
  contactPhone: string;
  pickUpStatus?: string;
  isPickUpAvailable?: boolean;
};

export const getFBPRequests = async (
  params: TRestoreRequestsPageParams,
): Promise<TRestoreRequestsPageResponse> => {
  const { filters, sorting, page, sellerWarehouseId } = params;
  const response = await sellerApiClient.get<TRestoreRequestsPageResponse>(
    "/v1/fbp/restock-requests/page",
    {
      params: { ...getParams(page, filters, sorting), sellerWarehouseId },
      paramsSerializer: { indexes: null },
    },
  );
  return response.data;
};

export const useFBPRequestsCounter = (sellerWarehouseId: string) => {
  const filters = [{
    name: "statuses",
    value: ["Created", "Accepted", "OnWay", "WarehouseReceiving"],
  }];
  const { data, refetch } = useQuery({
    queryKey: [
      "getFBPRequests",
      sellerWarehouseId,
      filters,
      ...Object.values(defaultParams),
    ],
    queryFn: () =>
      getFBPRequests({
        filters,
        page: defaultParams,
        sellerWarehouseId,
      }),
    retry: 1,
    select: ({ total }) => total ?? 0,
    enabled: Boolean(sellerWarehouseId),
  });
  return { data, refetch };
};

export const getFBPRequestDetails = async (
  id: string,
): Promise<TFBPRequestDetails> => {
  const response = await sellerApiClient.get<TFBPRequestDetails>(
    `/v1/fbp/restock-requests/${id}`,
  );
  return response.data;
};

type TFileResponse = {
  data: string;
  filename: string;
};

export const getFBPRequestLabel = async ({
  id,
}: {
  id: string;
}): Promise<TFileResponse> => {
  const response = await sellerApiClient.get<string>(
    `/v1/fbp/restock-requests/${id}/label`,
    { responseType: "blob" },
  );
  return {
    data: response.data,
    filename:
      (response.headers?.get &&
        response.headers // @ts-ignore
          .get("content-disposition")
          .match(/filename=([^;]+)/i)[1]) ||
      "order.zip",
    // contentType: response.headers["content-type"],
  };
};

export const getFBPRequestSKULabel = async ({
  id,
}: {
  id: string;
}): Promise<TFileResponse> => {
  const response = await sellerApiClient.get(
    `/v1/fbp/restock-requests/${id}/sku/labels`,
    { responseType: "blob" },
    // { params: { fileFormat, fileType }, }
  );
  return {
    data: response.data,
    filename:
      (response.headers?.get &&
        response.headers // @ts-ignore
          .get("content-disposition")
          .match(/filename=([^;]+)/i)[1]) ||
      "sku-labels.zip",
    // contentType: response.headers["content-type"],
  };
};

export interface IValidationData {
  ymProducts: {
    ymProductId: string;
    name: string;
    sku: string;
    url: string;
    primaryImage: string;
    count: number;
    errorType: string;
    dimensions?: {
      length: TSize;
      width: TSize;
      height: TSize;
    };
    weight?: TSize;
    density: {
      unit: string;
      value: number;
    };
  }[];
  wbProducts: {
    wbProductId: string;
    name: string;
    wbNmId: string;
    url: string;
    primaryImage: string;
    count: number;
    wbTechSize: string;
    errorType: string;
    dimensions?: {
      length: TSize;
      width: TSize;
      height: TSize;
    };
    weight?: TSize;
    density: {
      unit: string;
      value: number;
    };
  }[];
  aliProducts: {
    aliProductId: string;
    name: string;
    skuId: string;
    skuCode: string;
    primaryImage: string;
    count: number;
    errorType: string;
    dimensions?: {
      length: TSize;
      width: TSize;
      height: TSize;
    };
    weight?: TSize;
    density: {
      unit: string;
      value: number;
    };
  }[];
  undefinedProducts: {
    sku: string;
    count: number;
    techSize: string;
  }[];
}

export const validateFBPRestockExcel = async ({
  sellerWarehouseId,
  file,
}: {
  sellerWarehouseId: string;
  file: File;
}): Promise<{ validItems: TValidationRequestItem[]; invalidItems: TValidationRequestItem[] }> => {
  const form = new FormData();
  form.append("document", file);
  // form.append("sellerWarehouseId", sellerWarehouseId);
  const response = await sellerApiClient.post<IValidationData>(
    `/v1/fbp/restock-requests/excel/validate`,
    form,
    {
      headers: { "Content-Type": "multipart/form-data" },
      params: { sellerWarehouseId },
      data: form,
    },
  );

  const items = [
    ...(response.data?.ymProducts ?? []).map((v) => ({
      marketplace: "Yandex",
      id: v.ymProductId,
      name: v.name,
      sku: v.sku,
      url: v.url,
      primaryImage: v.primaryImage,
      count: +v.count,
      errorType: v.errorType,
      dimensions: v.dimensions,
      weight: v.weight,
      density: v.density,
    })),
    ...(response.data?.wbProducts ?? []).map((v) => ({
      marketplace: "Wildberries",
      id: v.wbProductId,
      name: v.name,
      wbNmId: v.wbNmId,
      url: v.url,
      primaryImage: v.primaryImage,
      count: +v.count,
      wbTechSize: v.wbTechSize,
      errorType: v.errorType,
      dimensions: v.dimensions,
      weight: v.weight,
      density: v.density,
    })),
    ...(response.data?.aliProducts ?? []).map((v) => ({
      marketplace: "Aliexpress",
      id: v.aliProductId,
      name: v.name,
      aliItemId: "",
      skuId: v.skuId,
      skuCode: v.skuCode,
      url: "",
      primaryImage: v.primaryImage,
      count: +v.count,
      errorType: v.errorType,
      dimensions: v.dimensions,
      weight: v.weight,
      density: v.density,
    })),
  ];

  return {
    validItems: items.filter((v) => !v.errorType),
    invalidItems: [
      ...items.filter((v) => v.errorType),
      ...(response.data?.undefinedProducts ?? []).map((v) => ({
        marketplace: "unknown",
        sku: v.sku,
        count: +v.count,
        techSize: v.techSize,
        id: "",
      })),
    ],
  };
};

export type TFBPStockItem = {
  id?: string; // добавляем для использования в таблицах
  matchingLinkName?: string;
  ymItems: {
    ymProduct: TYmItem;
    present: number;
  }[];
  wbItems: {
    wbProduct: TWbItem;
    present: number;
  }[];
  aliItems: {
    aliProduct: TAliItem;
    present: number;
  }[];
};

export type TFBPStocksRequest = TRequestParams & {
  sellerWarehouseId: string;
  hasStock?: boolean;
  includeNonPositivePresent?: boolean;
};
export type TFBPStocksResponse = {
  total: number;
  items: TFBPStockItem[];
};

function simpleHash(str: string) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash |= 0;
  }
  return String(Math.abs(hash));
}

/** Переключатель новой версии запроса стоков: `?newVersion=true` в адресе страницы. */
export const isFBPStocksNewVersion = () =>
  new URLSearchParams(window.location.search).get("newVersion") === "true";

export const getFBPStocks = async ({
  filters,
  sorting,
  page,
  sellerWarehouseId,
  hasStock = false,
  includeNonPositivePresent = false,
}: TFBPStocksRequest): Promise<TFBPStocksResponse> => {
  const response = await sellerApiClient.get<TFBPStocksResponse>(
    `/v1/fbp/stocks/page`,
    {
      params: {
        ...getParams(page, filters, sorting),
        // wbSellerId: sellerWarehouseId,
        sellerWarehouseId,
        hasStock,
        ...(isFBPStocksNewVersion() ? { newVersion: true } : {}),
      },
      paramsSerializer: { indexes: null },
    },
  );
  return {
    ...response.data,
    items: (response.data.items ?? [])
      .map((v) => {
        const isVisibleStockItem = (item: { present: number }) => (
          includeNonPositivePresent || (item.present ?? 0) > 0
        );
        const wbItems = (v.wbItems ?? [])
          .filter(isVisibleStockItem)
          .map((item) => ({
            ...item,
            wbProduct: { ...item.wbProduct, marketplace: "Wildberries" },
          }));
        const ymItems = (v.ymItems ?? [])
          .filter(isVisibleStockItem)
          .map((item) => ({
            ...item,
            ymProduct: { ...item.ymProduct, marketplace: "Yandex" },
          }));
        const aliItems = (v.aliItems ?? [])
          .filter(isVisibleStockItem)
          .map((item) => ({
            ...item,
            aliProduct: {
              ...item.aliProduct,
              marketplace: "Aliexpress",
              url: getAliProductUrl(item.aliProduct.aliItemId),
            },
          }));
        return {
          ...v,
          wbItems,
          ymItems,
          aliItems,
          id: simpleHash(
            [
              ...wbItems.map((i) => i.wbProduct.id),
              ...ymItems.map((i) => i.ymProduct.id),
              ...aliItems.map((i) => i.aliProduct.id),
            ].join(""),
          ),
        };
      })
      .filter((v) => v.wbItems.length + v.ymItems.length + v.aliItems.length > 0),
  };
};

export type TFBPLowStockCountResponse = {
  outOfStockCount: number;
  lowStockCount: number;
};

export const getFBPLowStockCount = async (
  sellerWarehouseId: string,
): Promise<TFBPLowStockCountResponse> => {
  const response = await sellerApiClient.get<TFBPLowStockCountResponse>(
    `/v1/fbp/stocks/seller-warehouses/${sellerWarehouseId}/low-stock-count`,
  );
  return response.data;
};

export const useFBPStocksCounter = (sellerWarehouseId: string) => {
  const { data, refetch } = useQuery({
    queryKey: [
      "getFBPStocks",
      sellerWarehouseId,
      ...Object.values(defaultParams),
    ],
    queryFn: () =>
      getFBPStocks({
        sellerWarehouseId,
        page: defaultParams,
        filters: [],
      }),
    retry: 1,
    select: ({ total }) => total ?? 0,
    enabled: Boolean(sellerWarehouseId),
  });
  return { data, refetch };
};

export type TYmMatchingItem = TYmItem & {
  matchingMemberId: string;
};

export type TWbMatchingItem = TWbItem & {
  matchingMemberId: string;
};

export type TAliMatchingItem = TAliItem & {
  matchingMemberId: string;
};

export type TFBPMatchingItem = {
  id: string;
  name: string;
  ymProducts: TYmMatchingItem[];
  wbProducts: TWbMatchingItem[];
  aliProducts: TAliMatchingItem[];
  isDeletionAvailable: boolean;
};

export type TFBPMatchingRequest = TRequestParams & {
  sellerWarehouseId: string;
};
export type TFBPMatchingResponse = {
  total: number;
  items: TFBPMatchingItem[];
};

export const getFBPMatchings = async ({
  filters,
  sorting,
  page,
  sellerWarehouseId,
}: TFBPMatchingRequest): Promise<TFBPMatchingResponse> => {
  const response = await sellerApiClient.get<TFBPMatchingResponse>(
    `/v1/fbp/matchings/page`,
    {
      params: { ...getParams(page, filters, sorting), sellerWarehouseId },
      paramsSerializer: { indexes: null },
    },
  );
  return {
    ...response.data,
    items: (response.data.items ?? []).map((v) => ({
      ...v,
      wbProducts: (v.wbProducts ?? []).map((item) => ({
        ...item,
        marketplace: "Wildberries",
      })),
      ymProducts: (v.ymProducts ?? []).map((item) => ({
        ...item,
        marketplace: "Yandex",
      })),
      aliProducts: (v.aliProducts ?? []).map((item) => ({
        ...item,
        marketplace: "Aliexpress",
        url: getAliProductUrl(item.aliItemId),
      })),
    })),
  };
};

export const useFBPMatchingCounter = (sellerWarehouseId: string) => {
  const { data, refetch } = useQuery({
    queryKey: [
      "getFBPMatchings",
      sellerWarehouseId,
      ...Object.values(defaultParams),
    ],
    queryFn: () =>
      getFBPMatchings({
        sellerWarehouseId,
        page: defaultParams,
        filters: [],
      }),
    retry: 1,
    select: ({ total }) => total ?? 0,
    enabled: Boolean(sellerWarehouseId),
  });
  return { data, refetch };
};

export const removeFBPMatching = async (id: string): Promise<void> => {
  const response = await sellerApiClient.delete<void>(
    `/v1/fbp/matchings/${id}`,
  );
  return response.data;
};

export type TMatchingRequestData = {
  sellerWarehouseId: string;
  name: string;
  ymProductIds: string[];
  wbProductIds: string[];
  aliProductIds: string[];
};

export const createFBPMatchingRequest = async (
  data: TMatchingRequestData,
): Promise<{ id: string }> => {
  const response = await sellerApiClient.post<{ id: string }>(
    `/v1/fbp/matchings`,
    { ...data },
  );
  return response.data;
};

export const getFBPProblematicRequests = async (
  params: TRestoreRequestsPageParams,
): Promise<TRestoreRequestsPageResponse> => {
  const { filters, sorting, page, sellerWarehouseId } = params;
  const response = await sellerApiClient.get<TRestoreRequestsPageResponse>(
    "/v1/fbp/restock-requests/problematic/page",
    {
      params: { ...getParams(page, filters, sorting), sellerWarehouseId },
      paramsSerializer: { indexes: null },
    },
  );
  return response.data;
};

export const useFBPProblematicRequestsCounter = (sellerWarehouseId: string) => {
  const filters = [{
    name: "statuses",
    value: ["Rejected"],
  }];
  const { data, refetch } = useQuery({
    queryKey: [
      "getFBPProblematicRequests",
      sellerWarehouseId,
      filters,
      ...Object.values(defaultParams),
    ],
    queryFn: () =>
      getFBPProblematicRequests({
        filters,
        page: defaultParams,
        sellerWarehouseId,
      }),
    retry: 1,
    select: ({ total }) => total ?? 0,
    enabled: Boolean(sellerWarehouseId),
  });
  return { data, refetch };
};

export interface IFBPPickupRequestData {
  address: {
    rawLine: string;
    street: string;
    city: string;
    cityDistrict: string;
    settlement: string;
    country: string;
    countryCode: string;
    zipCode: string;
    region: string;
    area: string;
    house: string;
    flat: string;
  };
  contactPhone: string;
  contactName: string;
  shipmentDate: string;
  shipmentTime: string;
  restockRequestId: string;
  comment: string;
}

export const createFBPPickupRequest = async (
  data: IFBPPickupRequestData,
): Promise<{ id: string }> => {
  const response = await sellerApiClient.post(`/v1/fbp-pick-up/order`, {
    ...data,
  });
  return response.data;
};

export type TValidateAddressRequest = {
  addressRawLine: string;
};

export type TValidateAddressResponse = {
  isValid: boolean;
};

export const validateAddress = async (
  data: TValidateAddressRequest,
): Promise<TValidateAddressResponse> => {
  const response = await sellerApiClient.post<TValidateAddressResponse>(
    "/v1/fbp-pick-up/validate-address",
    data,
  );
  return response.data;
};

export type TIfMatchingDeletionAvailable = {
  isAvailable: boolean;
};

export const getIfMatchingDeletionAvailable = async ({
  id,
}: {
  id: string;
}): Promise<TIfMatchingDeletionAvailable> => {
  const response = await sellerApiClient.get<TIfMatchingDeletionAvailable>(
    `/v1/fbp/matchings/${id}/is-deletion-available`,
  );
  return response.data;
};

export const deleteMatchingItems = async ({
  id,
  ymMemberIds,
  wbMemberIds,
  aliMemberIds,
}: {
  id: string;
  ymMemberIds: string[];
  wbMemberIds: string[];
  aliMemberIds: string[];
}): Promise<void> => {
  const response = await sellerApiClient.delete<void>(
    `/v1/fbp/matchings/${id}/members`,
    {
      data: {
        ymMemberIds,
        wbMemberIds,
        aliMemberIds,
      },
    },
  );
  return response.data;
};

export const updateMatching = async ({
  id,
  name,
  ymProductIds,
  wbProductIds,
  aliProductIds,
}: {
  id: string;
  name: string;
  ymProductIds: string[];
  wbProductIds: string[];
  aliProductIds: string[];
}): Promise<void> => {
  const response = await sellerApiClient.put<void>(`/v1/fbp/matchings/${id}`, {
    name,
    ymProductIds,
    wbProductIds,
    aliProductIds,
  });
  return response.data;
};

export const mergeSellers = async (params: {
  newSellerWarehouseName: string;
  sellerWarehouseIds: string[];
}): Promise<void> => {
  const response = await sellerApiClient.post<void>(
    "/v1/fbp/warehouses/seller/merge",
    params,
  );
  return response.data;
};

export type TWarehouseSellerDetails = {
  id: string,
  ymWarehouses: {
    id: string;
    name: string;
    ymCampaignId: string;
    sellerId: string;
    isAwaitingDeletion: boolean;
    placementType?: string;
  }[],
  aliWarehouses: {
    id: string;
    name: string;
    sellerId: string;
    tokenStatus?: string;
    isAwaitingDeletion: boolean;
    // Название шаблона доставки, зафиксированное при привязке
    shippingTemplateName: string;
  }[],
  wbWarehouses: {
    id: string;
    wbSellerId: string;
    warehouseName: string;
    sellerId: string;
    warehouseId: string;
    sellerName: string;
    tokenStatus?: string;
    isAwaitingDeletion: boolean;
  }[];
  name: string;
}

export const getSellerDetails = async (id: string): Promise<TWarehouseSellerDetails> => {
  const response = await sellerApiClient.get(
    `/v1/fbp/warehouses/sellers/${id}`
  );
  return response.data;
};

export const moveSellers = async (params: {
  destinationSellerWarehouseId?: string,
  newSellerWarehouseName?: string,
  wbWarehouseIds: string[],
  ymWarehouseIds: string[],
}): Promise<void> => {
  const response = await sellerApiClient.post<void>(
    "/v1/fbp/warehouses/warehouses/move",
    params,
  );
  return response.data;
};

export type TWithdrawalRequestsPageItem = {
  id: string;
  number: string;
  ymProductsCount: number;
  wbProductsCount: number;
  aliProductsCount: number;
  ymTotalWeight: TSize;
  wbTotalWeight: TSize;
  aliTotalWeight: TSize;
  createdAt: string;
  status: string;
  isCancelable: boolean;
  disposalDate?: string;
};

export type TWithdrawalRequestsPageParams = TRequestParams & {
  sellerWarehouseId: string;
};

export type TWithdrawalRequestsPageResponse = {
  total: number;
  items: TWithdrawalRequestsPageItem[];
};

export const getFBPWithdrawalRequests = async (
  params: TWithdrawalRequestsPageParams,
): Promise<TWithdrawalRequestsPageResponse> => {
  const { filters, sorting, page, sellerWarehouseId } = params;
  const response = await sellerApiClient.get<TWithdrawalRequestsPageResponse>(
    "/v1/fbp/withdrawal-requests/page",
    {
      params: { ...getParams(page, filters, sorting), sellerWarehouseId },
      paramsSerializer: { indexes: null },
    },
  );
  return response.data;
};

export const useFBPWithdrawalRequestsCounter = (sellerWarehouseId: string) => {
  const filters = [{
    name: "statuses",
    value: ["Created", "Accepted", "Assembled"],
  }];
  const { data, refetch } = useQuery({
    queryKey: [
      "getFBPWithdrawalRequests",
      sellerWarehouseId,
      filters,
      ...Object.values(defaultParams),
    ],
    queryFn: () =>
      getFBPWithdrawalRequests({
        filters,
        page: defaultParams,
        sellerWarehouseId,
      }),
    retry: 1,
    select: ({ total }) => total ?? 0,
    enabled: Boolean(sellerWarehouseId),
  });
  return { data, refetch };
};

export type TWithdrawalProductCommon = {
  id: string;
  url: string,
  primaryImage: string;
  name: string;
  dimensions?: {
    length: TSize;
    width: TSize;
    height: TSize;
  };
  weight?: TSize;
}

export type TWithdrawalProductWb = TWithdrawalProductCommon & {
  wbNmId: string;
  wbTechSize: string;
  count: number;
}

export type TWithdrawalProductYm = TWithdrawalProductCommon & {
  sku: string;
}

export type TWithdrawalProductAli = TWithdrawalProductCommon & {
  aliItemId: string;
  skuId: string;
  skuCode: string;
}

export type TWithdrawalItemWb = {
  wbProduct: TWithdrawalProductWb;
  count: number;
}

export type TWithdrawalItemYm = {
  ymProduct: TWithdrawalProductWb;
  count: number;
}

export type TWithdrawalItemAli = {
  aliProduct: TWithdrawalProductAli;
  count: number;
}

export type TWithdrawalDetails = {
  id: string;
  createdAt: string;
  disposalDate: string;
  status: string;
  isCancelable: boolean;
  number: string;
  sellerWarehouseId: string;
  ymItems: TWithdrawalItemYm[];
  wbItems: TWithdrawalItemWb[];
  aliItems: TWithdrawalItemAli[];
}

export const getWithdrawalDetails = async (id: string): Promise<TWithdrawalDetails> => {
  const response = await sellerApiClient.get(
    `/v1/fbp/withdrawal-requests/${id}`
  );
  return response.data;
};

export type TCreateWithdrawalRequestData = {
  sellerWarehouseId: string;
  ymProducts: {
    ymProductId: string;
    count: number;
  }[];
  wbProducts: {
    wbProductId: string;
    count: number;
  }[];
  // Опционально: вывоз при удалении магазина Ali пока не поддержан (DelStoreModal шлёт только ym/wb)
  aliProducts?: {
    aliProductId: string;
    count: number;
  }[];
}

export const createFBPWithdrawalRequest = async (
  data: TCreateWithdrawalRequestData,
): Promise<void> => {
  const response = await sellerApiClient.post<void>(
    `/v1/fbp/withdrawal-requests`,
    { ...data },
  );
  return response.data;
};

export const cancelFBPWithdrawalRequest = async (id: string): Promise<void> => {
  const response = await sellerApiClient.post<void>(
    `/v1/fbp/withdrawal-requests/${id}/cancel`,
  );
  return response.data;
};

// Удаление магазина из связки
export const removeMatchingWarehouse = async ({ id, marketplaceType }: {
  id: string,
  marketplaceType: string,
}): Promise<void> => {
  const response = await sellerApiClient.delete<void>(
    `/v1/fbp/warehouses/warehouses/${marketplaceType}/${id}`,
  );
  return response.data;
};

// Удаление связки
export const removeMatchingSeller = async (id: string): Promise<void> => {
  const response = await sellerApiClient.delete<void>(
    `/v1/fbp/warehouses/seller/${id}`,
  );
  return response.data;
};

export type TBlockingReason = {
  reason: string; // RestockRequests AssemblyOrders WithdrawalRequests
  blockedNumbers: string[];
}

export type TBlockedWarehouses = {
  isDeletionBlocked: boolean;
  ymWarehouses: {
    id: string;
    name: string;
    blockingReasons: TBlockingReason[];
    isMovingBlocked: boolean;
    isDeletionBlocked: boolean;
  }[];
  wbWarehouses: {
    id: string;
    name: string;
    blockingReasons: TBlockingReason[];
    isMovingBlocked: boolean;
    isDeletionBlocked: boolean;
  }[];
}

// Ручка для проверки заблокированных магазинов в связке и самой связки
export const getBlockedWarehouses = async (id: string): Promise<TBlockedWarehouses> => {
  const response = await sellerApiClient.get(
    `/v1/fbp/warehouses/seller/${id}/blocked-warehouses`
  );
  return response.data;
};

export type TFBPWartehouseStockItem = {
  id?: string;
  productId: string;
  sku: string;
  url: string;
  size: string;
  primaryImage: string;
  name: string;
  price?: TPrice;
  dimensions?: {
    length: TSize;
    width: TSize;
    height: TSize;
  };
  weight?: TSize;
  sellerName: string;
  present: number;
  matchingLinkName?: string; // todo
};

export type TFBPWarehouseStocksResponse = {
  total: number;
  items: TFBPWartehouseStockItem[];
};

export const getFBPWarehouseStocks = async ({
  filters,
  sorting,
  page,
  warehouseId,
  marketplaceType,
}: TRequestParams & {
  warehouseId: string;
  marketplaceType: string;
}): Promise<TFBPWarehouseStocksResponse> => {
  const response = await sellerApiClient.get<TFBPWarehouseStocksResponse>(
    `/v1/fbp/stocks/warehouses/page`,
    {
      params: {
        ...getParams(page, filters, sorting),
        warehouseId,
        marketplaceType,
      },
      paramsSerializer: { indexes: null },
    },
  );
  return response.data;
};
